import { ChevronDownIcon, ChevronUpIcon } from '@shared/assets/icons';
import { SelectOption, SelectValue } from '@shared/ui/select';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { useEffect, useRef, useState } from 'react';
import { Chip } from '@shared/ui/select/ui/Chips';
import { Checkbox } from '@shared/ui/kit';
import { Popover } from '@shared/ui/popover';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';

const NOOP = () => {};

interface SelectProps {
  label?: string;
  placeholder?: string;
  options?: SelectOption[];
  selectedValue?: SelectValue | SelectValue[];
  onChange: (value: SelectValue | SelectValue[]) => void;
  isDisabled?: boolean;
  isWithChips?: boolean;
  selectAllLabel?: string;
  listedChipsVariant?: 'default' | 'brandGhost';
}

export const Select = ({
  label,
  placeholder,
  options,
  selectedValue,
  isDisabled,
  onChange,
  isWithChips,
  selectAllLabel,
  listedChipsVariant,
}: SelectProps) => {
  const [dropdownWidth, setDropdownWidth] = useState<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpenSelect, setIsOpenSelect] = useState(false);
  const isMultiple = Array.isArray(selectedValue);

  const onClickSelect = () => {
    if (isDisabled) return;
    setIsOpenSelect(!isOpenSelect);
  };

  const onChangeItem = (value: SelectValue) => {
    if (!isMultiple) return onChange(value);

    if (selectedValue.includes(value)) {
      const newValue = selectedValue.filter((item) => item !== value);
      return onChange(newValue);
    }

    onChange([...selectedValue, value]);
  };

  const onClearItems = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onChange([]);
  };

  const isChecked = (value: SelectValue) => {
    if (!isMultiple) return value === selectedValue;

    return selectedValue.includes(value);
  };

  const optionValues = options?.map(({ value }) => value) ?? [];
  const isAllSelected =
    isMultiple && !!optionValues.length && optionValues.every((value) => selectedValue.includes(value));
  const isSomeSelected = isMultiple && !!selectedValue.length && !isAllSelected;
  const isWithSelectAll = isMultiple && !!selectAllLabel && !!options?.length;

  const onToggleAll = () => {
    if (!isMultiple) return;

    if (isAllSelected) {
      onChange([]);
      return;
    }

    onChange(optionValues);
  };

  useEffect(() => {
    if (buttonRef.current) {
      const width = buttonRef.current.offsetWidth;
      setDropdownWidth(width);
    }
  }, [selectedValue]);

  return (
    <div {...stylex.props(styles.container)}>
      {label && <span {...stylex.props(styles.label)}>{label}</span>}

      <UnstyledButton
        {...stylex.props(styles.select, isOpenSelect && styles.selectOpen)}
        ref={buttonRef}
        onClick={onClickSelect}
      >
        <>
          {!selectedValue || (isMultiple && !selectedValue.length) ? (
            placeholder
          ) : isMultiple ? (
            <span {...stylex.props(styles.multipleValue)}>
              Выбрано{' '}
              <Chip variant="counter" onDelete={onClearItems}>
                {selectedValue.length}
              </Chip>
            </span>
          ) : (
            options?.find(({ value }) => value === selectedValue)?.name
          )}

          {isOpenSelect ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </>
      </UnstyledButton>

      <Popover
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorRef={buttonRef}
        open={isOpenSelect}
        {...stylex.props(styles.popover(dropdownWidth))}
        onClose={() => setIsOpenSelect(false)}
      >
        <div {...stylex.props(styles.menuList)}>
          {isWithSelectAll && (
            <>
              <UnstyledButton onClick={onToggleAll} {...stylex.props(styles.item)}>
                <Checkbox onChange={NOOP} isChecked={isAllSelected} isIndeterminate={isSomeSelected} size="small" />
                <span {...stylex.props(styles.name)}>{selectAllLabel}</span>
              </UnstyledButton>
              <div {...stylex.props(styles.optionsDivider)} />
            </>
          )}

          {options?.map(({ value, name }) => (
            <UnstyledButton
              key={value}
              onClick={() => onChangeItem(value)}
              {...stylex.props(styles.item, !isMultiple && isChecked(value) && styles.selectedItem)}
            >
              {isMultiple && <Checkbox onChange={() => {}} isChecked={isChecked(value)} size="small" />}

              <span {...stylex.props(styles.name)}>{name}</span>
            </UnstyledButton>
          ))}
        </div>
      </Popover>

      {isWithChips && isMultiple && !!selectedValue.length && (
        <div {...stylex.props(styles.chips)}>
          {selectedValue.map((value) => (
            <Chip key={value} variant={listedChipsVariant} onDelete={() => onChangeItem(value)}>
              {options?.find((option) => option.value === value)?.name}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
};
